import { useState, useEffect } from 'react';
import mapFieldsToState from '../lib/mapFieldsToState';
import getVariantsFormFields from '../lib/getVariantsFormFields';
import { getTicketGroupsFormState } from '../lib/getTicketGroupsFormState';
import { isEqual } from 'lodash';
import { formatPriceInput } from '../services/formatPrice';
import usePrevious from '../hooks/usePrevious';

export default function useCheckoutForm({
  checkout,
  product,
  query,
  countryCode,
  onPayWhatYouWantPriceChange,
  draftCheckout,
  isPreview,
}) {
  const [form, setForm] = useState({
    discountCode: '',
    fields: checkout.fields && mapFieldsToState(checkout.fields, query, countryCode, draftCheckout),
    variants: product.variants && getVariantsFormFields(product.variants, query, draftCheckout),
    ticketGroups:
      checkout.ticketGroups &&
      getTicketGroupsFormState(checkout.ticketGroups, query, draftCheckout),
    payWhatYouWant: query.pwyw
      ? Number(query.pwyw / 100)
      : draftCheckout?.payWhatYouWantPrice
        ? Number(draftCheckout?.payWhatYouWantPrice / 100)
        : null,
  });

  const previousQuery = usePrevious(query);

  // update the form state when the query changes
  // used to update fields, variants and pay what you want price
  useEffect(() => {
    if (isEqual(query, previousQuery)) return;

    setForm(prevForm => ({
      ...prevForm,
      fields:
        checkout.fields && mapFieldsToState(checkout.fields, query, countryCode, draftCheckout),
      variants: product.variants && getVariantsFormFields(product.variants, query, draftCheckout),
      ticketGroups:
        checkout.ticketGroups &&
        getTicketGroupsFormState(checkout.ticketGroups, query, draftCheckout),
      payWhatYouWant: query.pwyw
        ? Number(query.pwyw / 100)
        : draftCheckout?.payWhatYouWantPrice
          ? Number(draftCheckout?.payWhatYouWantPrice / 100)
          : null,
    }));
  }, [query]);

  /**
   * PREVIEW USE EFFECTS BEGIN!
   *
   * In our dashboard we have a preview component that allows us to directly render store (starting with the translation and then checkout state providers)
   * In the dashboard, merchants can update their store and the changes are reflected instantly. To keep the fields, variants and tickets reactive we
   * set the state of the form each time these change. This will reset the values in the respective entity. E.g. if the dashboard sends an update
   * to update fields, it will clear all values in fields (rerendered). This is why we use an !isPreview check to guard against this clearing of values
   * in a production environment.
   *
   * Removing/altering these useEffects has multiple side effects - it can break the dashboard being reactive, it can create the dashboard to error out
   * it can clear the data in the forms and more. If you change these please be extra cautious.
   *
   * TL;DR be careful!
   */
  useEffect(() => {
    if (!isPreview) return;

    setForm(prevForm => ({
      ...prevForm,
      fields:
        checkout.fields && mapFieldsToState(checkout.fields, query, countryCode, draftCheckout),
    }));
  }, [checkout.fields, isPreview]);

  useEffect(() => {
    if (!isPreview) return;

    setForm(prevForm => ({
      ...prevForm,
      variants: product.variants && getVariantsFormFields(product.variants, query, draftCheckout),
    }));
  }, [product.variants, isPreview]);

  useEffect(() => {
    if (!isPreview) return;

    setForm(prevForm => ({
      ...prevForm,
      ticketGroups:
        checkout.ticketGroups &&
        getTicketGroupsFormState(checkout.ticketGroups, query, draftCheckout),
    }));
  }, [checkout.ticketGroups, isPreview]);
  /**
   * PREVIEW USE EFFECTS END!
   */

  // update pay what you want price when form changes
  useEffect(() => {
    onPayWhatYouWantPriceChange?.(formatPriceInput(form.payWhatYouWant, product.currency));
  }, [form.payWhatYouWant, onPayWhatYouWantPriceChange, product.currency]);

  return {
    form,
    setForm,
  };
}
